import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../../components/ui/Input';
import { Checkbox } from '../../components/ui/Checkbox';
import Button from '../../components/ui/Button';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import Icon from '../../components/AppIcon';
import { cn } from '../../utils/cn';
import { supabase } from '../../lib/supabase';
import auditLogService from '../../services/auditLogService';

const Register = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false
  });
  const [errors, setErrors] = useState({});
  const [validationState, setValidationState] = useState({
    fullName: null, // null, 'valid', 'invalid'
    email: null,
    password: null,
    confirmPassword: null
  });
  const [isLoading, setIsLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState('');
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [showResendEmail, setShowResendEmail] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [authError, setAuthError] = useState(null);

  // Real-time full name validation
  const validateFullName = (name) => {
    if (!name?.trim()) {
      return { valid: false, message: 'Full name is required' };
    }
    if (name?.trim()?.length < 2) {
      return { valid: false, message: 'Name must be at least 2 characters' };
    }
    return { valid: true, message: '' };
  };

  // Real-time email validation
  const validateEmail = (email) => {
    if (!email?.trim()) {
      return { valid: false, message: 'Email is required' };
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex?.test(email)) {
      return { valid: false, message: 'Please enter a valid email address' };
    }
    return { valid: true, message: '' };
  };

  // Real-time password validation with strength requirements
  const validatePassword = (password) => {
    if (!password) {
      return { valid: false, message: 'Password is required' };
    }
    if (password?.length < 8) {
      return { valid: false, message: 'Password must be at least 8 characters' };
    }
    if (!/[A-Z]/?.test(password)) {
      return { valid: false, message: 'Password must contain at least one uppercase letter' };
    }
    if (!/[a-z]/?.test(password)) {
      return { valid: false, message: 'Password must contain at least one lowercase letter' };
    }
    if (!/[0-9]/?.test(password)) {
      return { valid: false, message: 'Password must contain at least one number' };
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/?.test(password)) {
      return { valid: false, message: 'Password must contain at least one special character' };
    }
    return { valid: true, message: '' };
  };

  // Real-time confirm password validation
  const validateConfirmPassword = (confirmPassword, password) => {
    if (!confirmPassword) {
      return { valid: false, message: 'Please confirm your password' };
    }
    if (confirmPassword !== password) {
      return { valid: false, message: 'Passwords do not match' };
    }
    return { valid: true, message: '' };
  };

  // Calculate password strength for visual indicator
  const calculatePasswordStrength = (password) => {
    if (!password) return '';
    
    let strength = 0;
    if (password?.length >= 8) strength++;
    if (password?.length >= 12) strength++;
    if (/[A-Z]/?.test(password)) strength++;
    if (/[a-z]/?.test(password)) strength++;
    if (/[0-9]/?.test(password)) strength++;
    if (/[!@#$%^&*(),.?":{}|<>]/?.test(password)) strength++;

    if (strength <= 2) return 'weak';
    if (strength <= 4) return 'medium';
    return 'strong';
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear errors when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    if (authError) {
      setAuthError(null);
    }

    // Real-time validation feedback
    if (field === 'fullName') {
      const validation = validateFullName(value);
      setValidationState(prev => ({ ...prev, fullName: validation?.valid ? 'valid' : 'invalid' }));
      if (!validation?.valid && value?.length > 0) {
        setErrors(prev => ({ ...prev, fullName: validation?.message }));
      } else {
        setErrors(prev => ({ ...prev, fullName: '' }));
      }
    }

    if (field === 'email') {
      const validation = validateEmail(value);
      setValidationState(prev => ({ ...prev, email: validation?.valid ? 'valid' : 'invalid' }));
      if (!validation?.valid && value?.length > 0) {
        setErrors(prev => ({ ...prev, email: validation?.message }));
      } else {
        setErrors(prev => ({ ...prev, email: '' }));
      }
    }

    if (field === 'password') {
      const validation = validatePassword(value);
      setValidationState(prev => ({ ...prev, password: validation?.valid ? 'valid' : 'invalid' }));
      setPasswordStrength(calculatePasswordStrength(value));
      if (!validation?.valid && value?.length > 0) {
        setErrors(prev => ({ ...prev, password: validation?.message }));
      } else {
        setErrors(prev => ({ ...prev, password: '' }));
      }
      
      // Re-validate confirm password if it has a value
      if (formData?.confirmPassword) {
        const confirmValidation = validateConfirmPassword(formData?.confirmPassword, value);
        setValidationState(prev => ({ ...prev, confirmPassword: confirmValidation?.valid ? 'valid' : 'invalid' }));
        if (!confirmValidation?.valid) {
          setErrors(prev => ({ ...prev, confirmPassword: confirmValidation?.message }));
        } else {
          setErrors(prev => ({ ...prev, confirmPassword: '' }));
        }
      }
    }

    if (field === 'confirmPassword') {
      const validation = validateConfirmPassword(value, formData?.password);
      setValidationState(prev => ({ ...prev, confirmPassword: validation?.valid ? 'valid' : 'invalid' }));
      if (!validation?.valid && value?.length > 0) {
        setErrors(prev => ({ ...prev, confirmPassword: validation?.message }));
      } else {
        setErrors(prev => ({ ...prev, confirmPassword: '' }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    const nameValidation = validateFullName(formData?.fullName);
    if (!nameValidation?.valid) {
      newErrors.fullName = nameValidation?.message;
      isValid = false;
    }

    const emailValidation = validateEmail(formData?.email);
    if (!emailValidation?.valid) {
      newErrors.email = emailValidation?.message;
      isValid = false;
    }

    const passwordValidation = validatePassword(formData?.password);
    if (!passwordValidation?.valid) {
      newErrors.password = passwordValidation?.message;
      isValid = false;
    }

    const confirmPasswordValidation = validateConfirmPassword(formData?.confirmPassword, formData?.password);
    if (!confirmPasswordValidation?.valid) {
      newErrors.confirmPassword = confirmPasswordValidation?.message;
      isValid = false;
    }

    if (!formData?.acceptTerms) {
      newErrors.acceptTerms = 'You must accept the terms and conditions';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const attemptRegistration = async () => {
    const { data, error } = await supabase?.auth?.signUp({
      email: formData?.email,
      password: formData?.password,
      options: {
        emailRedirectTo: `${window.location?.origin}/onboarding-agent`,
        data: {
          email: formData?.email,
          full_name: formData?.fullName
        }
      }
    });

    if (error) {
      throw error;
    }

    return data;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});
    setAuthError(null);

    try {
      // Attempt registration
      const data = await attemptRegistration();

      // Successful registration
      // Log registration event
      if (data?.user) {
        await auditLogService?.createLog(
          data?.user?.id,
          auditLogService?.EVENT_TYPES?.REGISTER,
          'New user account created',
          { success: true, metadata: { email: formData?.email } }
        );
      }

      // Check if email confirmation is required
      if (data?.user && !data?.session) {
        // Email confirmation required
        setRegistrationSuccess(true);
        setShowResendEmail(true);
        setIsLoading(false);
      } else if (data?.session) {
        // Auto-login successful (email confirmation disabled)
        navigate('/onboarding-agent');
      }
    } catch (error) {
      console.error('Registration error:', error);
      
      // Handle specific Supabase auth errors with detailed messages
      if (error?.message?.includes('User already registered') || error?.message?.includes('already exists')) {
        setAuthError('Account already exists. Please sign in or use a different email address.');
        setErrors({ email: 'An account with this email already exists' });
      } else if (error?.message?.includes('Password should be at least')) {
        setAuthError('Password does not meet security requirements.');
        setErrors({ password: error?.message });
      } else if (error?.message?.includes('Password is too weak')) {
        setAuthError('Password is too weak. Please use a stronger password with uppercase, lowercase, numbers, and special characters.');
        setErrors({ password: 'Password does not meet strength requirements' });
      } else if (error?.message?.includes('Too many requests')) {
        setAuthError('Too many registration attempts. Please wait a few minutes before trying again.');
      } else if (error?.message?.includes('Network') || error?.message?.includes('network')) {
        setAuthError('Network error. Please check your internet connection and try again.');
      } else if (error?.code === 'PGRST301') {
        setAuthError('Service temporarily unavailable. Please try again in a moment.');
      } else {
        setAuthError(error?.message || 'Registration failed. Please try again.');
      }
      
      setIsLoading(false);
    }
  };

  const handleSignInClick = () => {
    navigate('/login');
  };

  const handleResendVerification = async () => {
    if (!formData?.email) {
      setErrors({ email: 'Email address is required' });
      return;
    }

    setResendLoading(true);
    setErrors({});

    try {
      const { error } = await supabase?.auth?.resend({
        type: 'signup',
        email: formData?.email
      });

      if (error) {
        setErrors({ email: error?.message || 'Failed to resend verification email' });
        setResendLoading(false);
        return;
      }

      // Show success message
      setErrors({ email: 'Verification email sent! Please check your inbox.' });
      setResendLoading(false);
      
      setTimeout(() => {
        setErrors({});
      }, 3000);
    } catch (err) {
      console.error('Resend verification error:', err);
      setErrors({ email: 'Failed to resend verification email' });
      setResendLoading(false);
    }
  };

  const getPasswordStrengthColor = () => {
    switch (passwordStrength) {
      case 'weak':
        return 'bg-red-500';
      case 'medium':
        return 'bg-yellow-500';
      case 'strong':
        return 'bg-green-500';
      default:
        return 'bg-gray-300';
    }
  };

  const getPasswordStrengthText = () => {
    switch (passwordStrength) {
      case 'weak':
        return 'Weak - Add more characters and variety';
      case 'medium':
        return 'Medium - Good, but could be stronger';
      case 'strong':
        return 'Strong - Excellent password!';
      default:
        return '';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary rounded-2xl mb-4 overflow-hidden">
            <img 
              src="/assets/images/Photo_Feb_21_2026_12_00_04_AM_1_-1771799585955.png" 
              alt="DraftIQ Logo" 
              className="w-full h-full object-contain p-2"
            />
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-2">Join DraftIQ</h1>
          <p className="text-muted-foreground">Create your account and start your multi-agent business journey</p>
        </div>

        {/* Success Message for Email Confirmation */}
        {registrationSuccess ? (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <Icon name="CheckCircle" size={32} className="text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">Check Your Email</h2>
              <p className="text-muted-foreground mb-6">
                We've sent a confirmation link to <strong>{formData?.email}</strong>. 
                Please check your inbox and click the link to verify your account.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-blue-800">
                  <Icon name="Info" size={16} className="inline mr-2" />
                  Didn't receive the email? Check your spam folder or contact support.
                </p>
              </div>
              {showResendEmail && (
                <div className="mb-6">
                  <button
                    type="button"
                    onClick={handleResendVerification}
                    disabled={resendLoading}
                    className="w-full px-4 py-2 text-sm text-primary hover:text-primary/80 border border-primary rounded-lg hover:bg-primary/5 transition-colors font-medium"
                  >
                    {resendLoading ? 'Sending...' : 'Resend Verification Email'}
                  </button>
                </div>
              )}
              <Button
                onClick={() => navigate('/login')}
                fullWidth
                size="lg"
              >
                Go to Login
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* Registration Form Card */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              {/* Global Auth Error Message */}
              {authError && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-red-800 font-medium">{authError}</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Full Name Field with Validation Indicator */}
                <div className="relative">
                  <Input
                    type="text"
                    label="Full Name"
                    placeholder="Enter your full name"
                    value={formData?.fullName}
                    onChange={(e) => handleInputChange('fullName', e?.target?.value)}
                    error={errors?.fullName}
                    required
                    disabled={isLoading}
                    className={cn(
                      validationState?.fullName === 'valid' && 'border-green-500 focus:border-green-500',
                      validationState?.fullName === 'invalid' && 'border-red-500 focus:border-red-500'
                    )}
                  />
                  {/* Validation Icon */}
                  {validationState?.fullName === 'valid' && (
                    <CheckCircle2 className="absolute right-3 top-9 w-5 h-5 text-green-600" />
                  )}
                  {validationState?.fullName === 'invalid' && formData?.fullName?.length > 0 && (
                    <AlertCircle className="absolute right-3 top-9 w-5 h-5 text-red-600" />
                  )}
                  {/* Inline Error Message */}
                  {errors?.fullName && (
                    <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors?.fullName}
                    </p>
                  )}
                </div>

                {/* Email Field with Validation Indicator */}
                <div className="relative">
                  <Input
                    type="email"
                    label="Email Address"
                    placeholder="Enter your email"
                    value={formData?.email}
                    onChange={(e) => handleInputChange('email', e?.target?.value)}
                    error={errors?.email}
                    required
                    disabled={isLoading}
                    className={cn(
                      validationState?.email === 'valid' && 'border-green-500 focus:border-green-500',
                      validationState?.email === 'invalid' && 'border-red-500 focus:border-red-500'
                    )}
                  />
                  {/* Validation Icon */}
                  {validationState?.email === 'valid' && (
                    <CheckCircle2 className="absolute right-3 top-9 w-5 h-5 text-green-600" />
                  )}
                  {validationState?.email === 'invalid' && formData?.email?.length > 0 && (
                    <AlertCircle className="absolute right-3 top-9 w-5 h-5 text-red-600" />
                  )}
                  {/* Inline Error Message */}
                  {errors?.email && (
                    <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors?.email}
                    </p>
                  )}
                </div>

                {/* Password Field with Strength Indicator */}
                <div className="relative">
                  <Input
                    type="password"
                    label="Password"
                    placeholder="Create a strong password"
                    value={formData?.password}
                    onChange={(e) => handleInputChange('password', e?.target?.value)}
                    error={errors?.password}
                    required
                    disabled={isLoading}
                    className={cn(
                      validationState?.password === 'valid' && 'border-green-500 focus:border-green-500',
                      validationState?.password === 'invalid' && 'border-red-500 focus:border-red-500'
                    )}
                  />
                  {/* Validation Icon */}
                  {validationState?.password === 'valid' && (
                    <CheckCircle2 className="absolute right-3 top-9 w-5 h-5 text-green-600" />
                  )}
                  {validationState?.password === 'invalid' && formData?.password?.length > 0 && (
                    <AlertCircle className="absolute right-3 top-9 w-5 h-5 text-red-600" />
                  )}
                  {/* Inline Error Message */}
                  {errors?.password && (
                    <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors?.password}
                    </p>
                  )}
                  {/* Password Strength Meter */}
                  {formData?.password && (
                    <div className="mt-2">
                      <div className="flex gap-1 mb-1">
                        <div className={cn("h-1.5 flex-1 rounded transition-colors", passwordStrength ? getPasswordStrengthColor() : 'bg-gray-300')} />
                        <div className={cn("h-1.5 flex-1 rounded transition-colors", passwordStrength === 'medium' || passwordStrength === 'strong' ? getPasswordStrengthColor() : 'bg-gray-300')} />
                        <div className={cn("h-1.5 flex-1 rounded transition-colors", passwordStrength === 'strong' ? getPasswordStrengthColor() : 'bg-gray-300')} />
                      </div>
                      <p className={cn(
                        "text-xs font-medium",
                        passwordStrength === 'weak' && 'text-red-600',
                        passwordStrength === 'medium' && 'text-yellow-600',
                        passwordStrength === 'strong' && 'text-green-600'
                      )}>
                        {getPasswordStrengthText()}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Must include: 8+ characters, uppercase, lowercase, number, special character
                      </p>
                    </div>
                  )}
                </div>

                {/* Confirm Password Field with Validation Indicator */}
                <div className="relative">
                  <Input
                    type="password"
                    label="Confirm Password"
                    placeholder="Re-enter your password"
                    value={formData?.confirmPassword}
                    onChange={(e) => handleInputChange('confirmPassword', e?.target?.value)}
                    error={errors?.confirmPassword}
                    required
                    disabled={isLoading}
                    className={cn(
                      validationState?.confirmPassword === 'valid' && 'border-green-500 focus:border-green-500',
                      validationState?.confirmPassword === 'invalid' && 'border-red-500 focus:border-red-500'
                    )}
                  />
                  {/* Validation Icon */}
                  {validationState?.confirmPassword === 'valid' && (
                    <CheckCircle2 className="absolute right-3 top-9 w-5 h-5 text-green-600" />
                  )}
                  {validationState?.confirmPassword === 'invalid' && formData?.confirmPassword?.length > 0 && (
                    <AlertCircle className="absolute right-3 top-9 w-5 h-5 text-red-600" />
                  )}
                  {/* Inline Error Message */}
                  {errors?.confirmPassword && (
                    <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors?.confirmPassword}
                    </p>
                  )}
                </div>

                {/* Terms and Conditions Checkbox */}
                <div>
                  <Checkbox
                    id="acceptTerms"
                    checked={formData?.acceptTerms}
                    onChange={(e) => handleInputChange('acceptTerms', e?.target?.checked)}
                    label={
                      <span className="text-sm">
                        I agree to the{' '}
                        <button
                          type="button"
                          className="text-primary hover:underline"
                          onClick={(e) => {
                            e?.preventDefault();
                            console.log('Navigate to terms of service');
                          }}
                        >
                          Terms of Service
                        </button>
                        {' '}and{' '}
                        <button
                          type="button"
                          className="text-primary hover:underline"
                          onClick={(e) => {
                            e?.preventDefault();
                            console.log('Navigate to privacy policy');
                          }}
                        >
                          Privacy Policy
                        </button>
                      </span>
                    }
                    disabled={isLoading}
                  />
                  {errors?.acceptTerms && (
                    <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors?.acceptTerms}
                    </p>
                  )}
                </div>

                {/* Create Account Button */}
                <Button
                  type="submit"
                  fullWidth
                  size="lg"
                  loading={isLoading}
                  disabled={isLoading}
                  className="mt-6"
                >
                  {isLoading ? 'Creating Account...' : 'Create Account'}
                </Button>
              </form>
            </div>

            {/* Footer */}
            <div className="text-center mt-6 text-sm text-muted-foreground">
              <p>
                Already have an account?{' '}
                <button
                  onClick={handleSignInClick}
                  disabled={isLoading}
                  className={cn(
                    "text-primary hover:underline font-medium",
                    (isLoading) && "opacity-50 cursor-not-allowed"
                  )}
                >
                  Sign In
                </button>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Register;